import { ActivityIndicator, Pressable, SxProp, View } from 'dripsy';
import * as DocumentPicker from 'expo-document-picker';
import React from 'react';
import { Icon } from '../icons/Icon';
import { TextCaption, TextTitle } from '../typography';
import { useUploadDocument } from './useUploadDocument.hook';

interface UploadDocumentProps {
  type?: string[] | string; // MIME type
  title?: string;
  instructions?: string;
  successUploadedInstructions?: string;
  onValueChange: (document: undefined | DocumentPicker.DocumentResult) => void;
  sx?: SxProp;
  isLoading?: boolean;
  isUploadSuccess?: boolean;
}

export const UploadDocument: React.FC<UploadDocumentProps> = ({
  type,
  title,
  instructions,
  onValueChange,
  sx,
  isLoading,
  isUploadSuccess,
  successUploadedInstructions
}) => {
  const { selectedDocument, pickDocument } = useUploadDocument({
    type,
    onValueChange
  });

  const displayTitle = title || 'Upload document';
  const displayInstructions = instructions || 'Please provide a document here. Click here to Upload.';
  const displaySuccessUploadedInstructions = successUploadedInstructions || 'Please provide a document here. Click here to Upload.';
  const uploaded = selectedDocument && !isLoading && isUploadSuccess;
  const icon = selectedDocument && !isLoading && isUploadSuccess ? 'documentUploaded' : 'uploadDocumentZeroState';

  return (
    <Pressable onPress={pickDocument} sx={{ ...styles.container, ...sx, borderColor: uploaded ? '$success' : '$borderUpload' }}>
      <View sx={styles.left}>
        <TextTitle bold sx={{ color: uploaded ? '$success' : '$secondary' }}>
          {displayTitle}
        </TextTitle>
        <TextCaption secondary sx={{}}>
          {uploaded ? displaySuccessUploadedInstructions : displayInstructions}
        </TextCaption>
      </View>
      <View sx={styles.right}>
        {isLoading ? (
          <>
            <View
              sx={{
                width: uploaded ? 50 : 60,
                height: uploaded ? 50 : 60,
                justifyItems: 'center'
              }}>
              <ActivityIndicator size={'large'} />
            </View>
          </>
        ) : (
          <>
            <Icon name={icon} width={uploaded ? 50 : 60} height={uploaded ? 50 : 60} />
          </>
        )}
        {!!selectedDocument && !isLoading && isUploadSuccess && (
          <TextCaption sx={{ color: '$success' }} bold>
            Uploaded
          </TextCaption>
        )}
      </View>
    </Pressable>
  );
};

enum StyleName {
  container = 'container',
  left = 'left',
  right = 'right'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    padding: '$7',
    borderRadius: 10,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '$borderUpload',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '$10'
  },
  left: {
    flex: 1,
    gap: '$2'
  },
  right: {
    alignItems: 'center'
  }
};
